import React from 'react';

/**
 * WebMCP — exposes GreatStore's key actions to in-browser AI agents via the
 * emerging WebMCP API (navigator.modelContext). If a real agent runtime is
 * present, our tools register with it; if not, we install a minimal spec-shaped
 * shim so the tools remain introspectable. Renders nothing.
 *
 * Spec: https://webmachinelearning.github.io/webmcp/
 */

const FEATURES: string[] = [
  'AI Builder — describe your store and generate a complete, on-brand storefront in minutes.',
  'Storefront Builder — fully customizable, responsive storefronts with no code.',
  'Page Builder — design any page with AI assistance.',
  'Category Pages — rich, conversion-ready category browsing.',
  'Product Pages — detailed, agent-readable product detail pages.',
  'Agent-ready — every store ships with a live MCP endpoint so AI agents can browse and buy.'
];

const MCP_ENDPOINT = 'https://demo.greatstore.ai/builder-mcp';
const MCP_CARD = 'https://demo.greatstore.ai/.well-known/mcp/server-card.json';
const DEMO_STORE = 'https://demo.greatstore.ai/';

const text = (t: string) => ({ content: [{ type: 'text', text: t }] });

const tools = [
  {
    name: 'browse_features',
    description: "List GreatStore's core capabilities for building AI-ready storefronts.",
    inputSchema: { type: 'object', properties: {}, additionalProperties: false },
    async execute() {
      return text('GreatStore capabilities:\n- ' + FEATURES.join('\n- '));
    }
  },
  {
    name: 'open_demo_store',
    description: "Open GreatStore's live demo storefront in a new browser tab.",
    inputSchema: { type: 'object', properties: {}, additionalProperties: false },
    async execute() {
      try { window.open(DEMO_STORE, '_blank', 'noopener'); } catch (e) {}
      return text('Opened the GreatStore demo storefront: ' + DEMO_STORE);
    }
  },
  {
    name: 'connect_commerce_mcp',
    description: "Get GreatStore's live MCP server endpoint so an AI agent can browse catalogs and shop programmatically.",
    inputSchema: { type: 'object', properties: {}, additionalProperties: false },
    async execute() {
      return text(
        'GreatStore MCP server (streamable-http, no auth required):\n' +
        'Endpoint: ' + MCP_ENDPOINT + '\n' +
        'Server card: ' + MCP_CARD
      );
    }
  },
  {
    name: 'book_demo',
    description: 'Get the link to book a GreatStore product demo.',
    inputSchema: { type: 'object', properties: {}, additionalProperties: false },
    async execute() {
      return text(
        'Book a GreatStore demo from the "Book a demo" button at https://www.greatstore.ai, ' +
        'or explore the live demo store now at ' + DEMO_STORE
      );
    }
  }
];

export const WebMCP = () => {
  React.useEffect(() => {
    try {
      const nav = navigator as any;
      if (!nav.modelContext) {
        const reg: any[] = [];
        Object.defineProperty(nav, 'modelContext', {
          configurable: true,
          value: {
            registerTool(t: any) { reg.push(t); return { unregister() {} }; },
            provideContext(ctx: any) { ((ctx && ctx.tools) || []).forEach((t: any) => reg.push(t)); },
            get tools() { return reg; }
          }
        });
      }
      const mc = nav.modelContext;
      // Native Chrome WebMCP uses registerTool(toolObject); older drafts use provideContext({ tools }).
      if (typeof mc.registerTool === 'function') {
        tools.forEach((t) => { try { mc.registerTool(t); } catch (e) {} });
      }
      if (typeof mc.provideContext === 'function') {
        try { mc.provideContext({ tools }); } catch (e) {}
      }
    } catch (e) {
      /* WebMCP unavailable in this environment — no-op */
    }
  }, []);
  return null;
};
